import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const StandardDeviationCalculatorPage = () => {
  const [inputData, setInputData] = useState('10, 12, 23, 23, 16, 23, 21, 16');
  const [isSample, setIsSample] = useState(true);
  const [result, setResult] = useState(null);

  const calculate = () => {
    const numbers = inputData.split(/[,\s]+/).map(n => parseFloat(n.trim())).filter(n => !isNaN(n));
    if (numbers.length < 2) {
      setResult({ error: 'Please enter at least 2 numbers' });
      return;
    }

    const n = numbers.length;
    const sum = numbers.reduce((a, b) => a + b, 0);
    const mean = sum / n;
    const squaredDiffs = numbers.map(x => Math.pow(x - mean, 2));
    const sumSquaredDiffs = squaredDiffs.reduce((a, b) => a + b, 0);
    
    const variance = isSample ? sumSquaredDiffs / (n - 1) : sumSquaredDiffs / n;
    const stdDev = Math.sqrt(variance);
    const coefficientOfVariation = (stdDev / mean) * 100;
    
    const sorted = [...numbers].sort((a, b) => a - b);
    const min = sorted[0];
    const max = sorted[n - 1];
    const range = max - min;
    const median = n % 2 === 0 ? (sorted[n/2 - 1] + sorted[n/2]) / 2 : sorted[Math.floor(n/2)];

    setResult({
      count: n,
      sum,
      mean,
      variance,
      stdDev,
      coefficientOfVariation,
      min,
      max,
      range,
      median,
      sorted
    });
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <Link to="/math" className="text-cyan-400 hover:text-cyan-300">Math</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Standard Deviation Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-4">Standard Deviation Calculator</h1>
          <p className="text-gray-400 mb-6">
            Calculate standard deviation, variance, mean, and other statistics for a set of numbers.
          </p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="space-y-4">
              <div>
                <label className="text-gray-300 font-medium block mb-2">Enter numbers (separated by commas or spaces)</label>
                <textarea
                  value={inputData}
                  onChange={(e) => setInputData(e.target.value)}
                  className="w-full h-24 px-4 py-3 bg-[#0d0d0d] border border-[#2a2a2a] rounded-lg text-white font-mono"
                  placeholder="10, 12, 23, 23, 16, 23, 21, 16"
                />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={isSample} onChange={() => setIsSample(true)} className="w-4 h-4 accent-cyan-500" />
                  <span className="text-gray-300">Sample (n-1)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={!isSample} onChange={() => setIsSample(false)} className="w-4 h-4 accent-cyan-500" />
                  <span className="text-gray-300">Population (n)</span>
                </label>
              </div>
            </div>

            <Button onClick={calculate} className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white px-8">
              Calculate
            </Button>

            {result && !result.error && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="text-center mb-6">
                  <p className="text-gray-400 mb-2">{isSample ? 'Sample' : 'Population'} Standard Deviation</p>
                  <p className="text-4xl font-bold text-green-400">σ = {result.stdDev.toFixed(6)}</p>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400 text-sm">Count (n)</p>
                    <p className="text-white font-semibold">{result.count}</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400 text-sm">Sum</p>
                    <p className="text-white font-semibold">{result.sum.toFixed(4)}</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400 text-sm">Mean (μ)</p>
                    <p className="text-white font-semibold">{result.mean.toFixed(4)}</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400 text-sm">Variance (σ²)</p>
                    <p className="text-white font-semibold">{result.variance.toFixed(4)}</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400 text-sm">Median</p>
                    <p className="text-white font-semibold">{result.median.toFixed(4)}</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400 text-sm">Range</p>
                    <p className="text-white font-semibold">{result.range.toFixed(4)}</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400 text-sm">Min</p>
                    <p className="text-white font-semibold">{result.min}</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400 text-sm">Max</p>
                    <p className="text-white font-semibold">{result.max}</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <p className="text-gray-400 text-sm">CV (%)</p>
                    <p className="text-white font-semibold">{result.coefficientOfVariation.toFixed(2)}%</p>
                  </div>
                </div>
              </div>
            )}

            {result && result.error && (
              <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400">{result.error}</p>
              </div>
            )}
          </div>

          <AdInContent />

          <div className="mt-10 space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Standard Deviation Formula</h2>
              <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                <p className="text-gray-300 font-mono text-center">σ = √[Σ(xᵢ - μ)² / (n - 1)]</p>
                <p className="text-gray-500 text-sm mt-2 text-center">For sample standard deviation. Use n for population.</p>
              </div>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">What is Standard Deviation?</h2>
              <p className="text-gray-400 leading-relaxed">
                Standard deviation is a measure of the amount of variation or dispersion in a set of values. 
                A low standard deviation indicates that values tend to be close to the mean, while a high 
                standard deviation indicates that values are spread out over a wider range.
              </p>
            </section>
          </div>

          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Math Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[{ name: 'Scientific', path: '/scientific-calculator' }, { name: 'Percentage', path: '/percentage-calculator' }, { name: 'Random Number', path: '/random-number-generator' }].map((calc) => (
                <Link key={calc.path} to={calc.path} className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 text-sm">{calc.name}</Link>
              ))}
            </div>
          </div>
          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default StandardDeviationCalculatorPage;
